import { useState } from 'react';

interface PageNumberInputProps {
  maxPage: number;
  onPageChange: (page: number) => void;
}

const PageNumberInput = ({ maxPage, onPageChange }: PageNumberInputProps) => {
  const [value, setValue] = useState('');
  const [hasNavigated, setHasNavigated] = useState(false);

  const maxDigits = String(Math.max(maxPage, 1)).length;
  const requestedPage = Number(value);
  const isOverMax = value !== '' && requestedPage > maxPage;
  const isValidPage =
    value !== '' && requestedPage >= 1 && requestedPage <= maxPage;
  const showClear = hasNavigated || isOverMax;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = event.target.value
      .replace(/[^0-9]/g, '')
      .slice(0, maxDigits);
    setValue(sanitized);
    if (hasNavigated) setHasNavigated(false);
  };

  const handleGo = () => {
    if (!isValidPage) return;
    onPageChange(requestedPage);
    setHasNavigated(true);
  };

  const handleClear = () => {
    setValue('');
    setHasNavigated(false);
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-sm font-medium text-cofeeNeutral8">
        Page no. (1-{maxPage})
      </span>
      <input
        inputMode="numeric"
        value={value}
        onChange={handleChange}
        onKeyDown={(event) => event.key === 'Enter' && handleGo()}
        placeholder="Enter"
        aria-invalid={isOverMax}
        className={`h-9 w-16 rounded-lg border px-2 text-center text-sm text-cofeeNeutral10 outline-none ${
          isOverMax
            ? 'border-error20 focus:border-error20'
            : 'border-cofeeNeutral4 focus:border-cofeePrimary4'
        }`}
      />
      <button
        type="button"
        onClick={showClear ? handleClear : handleGo}
        className="min-w-9 text-sm font-semibold text-cofeePrimary6"
      >
        {showClear ? 'Clear' : 'Go'}
      </button>
    </div>
  );
};

export default PageNumberInput;
